import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ArrowRight, MapPin, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || window.location.origin);

export default function PortfolioPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const resp = await axios.get(`${API}/api/portfolio`);
      setProjects(resp.data);
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const sectors = [...new Set(projects.map(p => p.sector).filter(Boolean))];
  const filtered = filter === "all" ? projects : projects.filter(p => p.sector === filter);

  return (
    <div className="min-h-screen" data-testid="portfolio-page">
      <section className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
            Nos <span className="text-[#0099CC]">realisations</span>
          </h1>
          <p className="text-[#94A3B8] mt-5 text-lg max-w-2xl mx-auto">
            Decouvrez les projets que nous avons realises pour nos clients.
            Chaque projet est concu sur mesure pour repondre aux besoins specifiques de chaque secteur.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="px-6 pb-8">
        <div className="max-w-6xl mx-auto flex flex-wrap justify-center gap-2">
          <button onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === "all" ? "bg-[#0099CC] text-white" : "bg-[#1E293B] text-[#94A3B8] hover:text-white border border-[#2A3441]"
            }`} data-testid="filter-all">Tous</button>
          {sectors.map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filter === s ? "bg-[#0099CC] text-white" : "bg-[#1E293B] text-[#94A3B8] hover:text-white border border-[#2A3441]"
              }`} data-testid={`filter-${s}`}>{s}</button>
          ))}
        </div>
      </section>

      {/* Projects Grid */}
      <section className="pb-24 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-[#151B2B] border border-[#2A3441] rounded-2xl h-80 animate-pulse" />
            ))
          ) : filtered.map((project) => (
            <div key={project.id}
              className="group bg-[#151B2B] border border-[#2A3441] rounded-2xl overflow-hidden hover:border-[#0099CC]/40 transition-all hover:shadow-lg hover:shadow-[#0099CC]/5"
              data-testid={`project-${project.id}`}>

              {/* Screenshot */}
              <div className="h-48 bg-[#0B0F19] relative overflow-hidden">
                {project.screenshot_path && project.screenshot_status === "ready" ? (
                  <img
                    src={`${API}/api/portfolio/screenshot/${project.id}`}
                    alt={project.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                ) : project.screenshot_status === "pending" ? (
                  <div className="w-full h-full bg-gradient-to-br from-[#0B0F19] to-[#1E293B] flex flex-col items-center justify-center gap-2">
                    <div className="h-6 w-6 border-2 border-[#0099CC] border-t-transparent rounded-full animate-spin" />
                    <span className="text-[#64748b] text-xs">Capture en cours...</span>
                  </div>
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#0B0F19] to-[#1E293B] flex items-center justify-center">
                    <span className="text-[#2A3441] text-4xl font-bold">{(project.name || "?")[0]}</span>
                  </div>
                )}
                {/* Overlay with visit link */}
                {project.url && (
                  <a href={project.url} target="_blank" rel="noopener noreferrer"
                    className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center"
                    data-testid={`visit-${project.id}`}>
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#0099CC] text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
                      <ExternalLink className="h-4 w-4" /> Visiter le site
                    </span>
                  </a>
                )}
              </div>

              <div className="p-5">
                <h3 className="text-white font-bold text-lg leading-tight">{project.name}</h3>

                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {project.sector && (
                    <span className="text-xs bg-[#0099CC]/15 text-[#0099CC] px-2 py-0.5 rounded-full">{project.sector}</span>
                  )}
                  {project.city_targets?.[0] && (
                    <span className="flex items-center gap-1 text-xs text-[#64748b]">
                      <MapPin className="h-3 w-3" />{project.city_targets[0]}
                    </span>
                  )}
                </div>

                <p className="text-[#94A3B8] text-sm mt-3 line-clamp-2">{project.description}</p>

                {project.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {project.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="flex items-center gap-1 text-[10px] bg-[#1E293B] text-[#64748b] px-2 py-0.5 rounded">
                        <Tag className="h-2.5 w-2.5" />{tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Direct link */}
                {project.url && (
                  <a href={project.url} target="_blank" rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#0099CC] hover:text-[#0088B5] transition-colors"
                    data-testid={`link-${project.id}`}>
                    <ExternalLink className="h-3 w-3" />
                    {project.url.replace(/^https?:\/\//, '')}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        {!loading && filtered.length === 0 && (
          <div className="text-center py-12">
            <p className="text-[#64748b]">Aucun projet pour ce filtre.</p>
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="py-16 px-6 border-t border-[#2A3441]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white">Votre projet est le prochain ?</h2>
          <p className="text-[#94A3B8] mt-3">Contactez-nous pour discuter de votre projet et obtenir un devis personnalise.</p>
          <Link to="/contact" className="mt-6 inline-block">
            <Button className="bg-[#0099CC] hover:bg-[#0088B5] text-white px-8 py-3 font-semibold" data-testid="portfolio-cta">
              Demander un devis <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
